import { gReactive } from '@gradin/vue-utils'
import { computed, ref } from 'vue'
import { useApi } from '@/functions/api'

export function useProductList () {
  const api = useApi()
  const products = ref<ProductList[]>([])
  const pagination = ref<Pagination>()
  const loading = ref(true)

  const getData = async (params = {}) => {
    try {
      const response = await api.GET<ApiCollection<ProductList>>('admin/product', params)
      products.value = response.data
      pagination.value = response.meta
    } finally {
      loading.value = false
    }
  }

  return {
    getData,
    loading,
    products,
    pagination,
  }
}

export function usePrintBarcodeProductList () {
  const api = useApi()
  const products = ref<ProductPrintBarcode[]>()
  const pagination = ref<Pagination>()
  const loading = ref(false)

  const getData = async (params = {}) => {
    try {
      loading.value = true
      const response = await api.GET<ApiCollection<ProductPrintBarcode>>('admin/print-barcode/product', params)
      products.value = response.data
      pagination.value = response.meta
    } finally {
      loading.value = false
    }
  }

  return {
    getData,
    loading,
    products,
    pagination,
  }
}

export function usePrintBarcodePurchaseList () {
  const api = useApi()
  const purchases = ref<PurchaseDraftPrintBarcode[]>()
  const pagination = ref<Pagination>()
  const loading = ref(false)

  const getData = async (params = {}) => {
    try {
      loading.value = true
      const response = await api.GET<ApiCollection<PurchaseDraftPrintBarcode>>('admin/print-barcode/purchase', params)
      purchases.value = response.data.filter(p => p.products.length > 0)
      pagination.value = response.meta
    } finally {
      loading.value = false
    }
  }

  return {
    getData,
    loading,
    purchases,
    pagination,
  }
}

export function useProductUpdate () {
  const api = useApi()
  const form = gReactive({
    name: '',
    price: 0,
    code: '',
    product_group_id: null as number | null,
    retail_ready_at: '',
    supplier_id: null as number | null,
  })
  const submitting = ref(false)
  const errors = ref<FormError>({})
  const product = ref<ProductList>()

  const submitForm = async (id: number) => {
    submitting.value = true
    try {
      const response = api.PUT<ProductList>(`admin/product/${id}`, form)
      product.value = await response
    } catch (error) {
      errors.value = api.formErrors(error)
    } finally {
      submitting.value = false
    }
  }

  return {
    form,
    submitForm,
    submitting,
    product,
    errors,
  }
}

export function useProductDelete () {
  const api = useApi()
  const submitting = ref(false)
  const deleted = ref(false)
  const errors = ref<FormError>({})
  const submitDelete = async (id: number) => {
    submitting.value = true
    deleted.value = false
    try {
      await api.DELETE(`admin/product/${id}`)
      deleted.value = true
    } catch (error) {
      errors.value = api.formErrors(error)
    } finally {
      submitting.value = false
    }
  }

  return {
    submitting,
    submitDelete,
    errors,
    deleted,
  }
}

export function useProductVariantFinder () {
  const api = useApi()
  const loading = ref(false)
  const product = ref<ProductVariantRes | null>()
  const findByCode = async (fullCode: string) => {
    product.value = undefined

    if (!fullCode) {
      return
    }

    try {
      loading.value = true
      const response = await api.GET<ProductVariantRes>(`admin/product-variant/${fullCode}`)
      product.value = response
    } catch {
      product.value = null
    } finally {
      loading.value = false
    }
  }

  const getProductVariant = async (code: string) => {
    const response = await api.GET<ApiCollection<ProductVariantSearchResource>>(`admin/product-variant/${encodeURIComponent(code)}`)
    return response.data
  }

  return {
    loading,
    product,
    findByCode,
    getProductVariant,
  }
}

export function useProductStockCheck () {
  const api = useApi()
  const loading = ref(false)
  const stockData = ref<ProductStockResponse | null>()

  const getStockByCode = async (code: string) => {
    stockData.value = null

    if (!code) {
      return
    }

    try {
      loading.value = true
      const response = await api.GET<ProductStockResponse>(`warehouse/product/stock/${code}`)
      stockData.value = response
    } catch {
      stockData.value = null
    } finally {
      loading.value = false
    }
  }

  const variants = computed(() => {
    return stockData.value?.data.map(variant => variant.variant) ?? []
  })
  const maxStock = computed(() => {
    const stocks = stockData.value?.data.flatMap(variant =>
      variant.stocks.map(stock => stock.stock),
    ) ?? []

    return Math.max(...stocks, 1) // Ensure at least 1 to avoid division by zero
  })

  const getHeatmapStyle = (stock: number) => {
    if (stock === 0) {
      return {
        backgroundColor: 'white',
      }
    }
    let opacity = stock / maxStock.value
    opacity = 0.1 + opacity * 0.9
    return {
      backgroundColor: `oklch(from var(--color-primary-500) l c h / ${opacity.toFixed(2)})`,
    }
  }

  return {
    loading,
    variants,
    stockData,
    getStockByCode,
    getHeatmapStyle,
  }
}
